/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import _ from "underscore";
import h from "util/dom-builder.js";
import moment from "dayjs";
import util from "../util/util.js";
import googleMaps from '../util/google-maps.js';
import tileLayer from '../map/generic-tile-layer.js';
import mapStyles from '../map/styles.js';
import areasMap from '../map/areas.js';
import sharedSitesMap from '../map/shared-sites.js';
import regionsMap from '../map/regions.js';
import SortableTableViewModel from "./shared/sortable-table.js";
import programs from "../programs.js";
import DensityMapType from '../map/density-map-type.js';

import "../util/knockout-pikaday.js";
import "./survey-summary.js";
import "./survey-summary-header.js";

var ProgramsRegionsViewModel = function(params) {
	document.body.classList.add("birdata-fullscreen");

	var self = this;

	self.loaded = ko.observable(false);
	self.isAdmin = ko.observable(false);

	self.map = ko.observable();
	self.mapOptions = ko.observable();
	self.mapBounds = ko.observable();

	var monthOptions = _.map(util.monthsShort(), function(m, i) {
		return {
			month: m,
			index: i + 1
		};
	});

	self.regionFilter = ko.observable(null);
	self.programFilter = ko.observable(null);
	self.afterDateFilter = ko.observable(null);
	self.beforeDateFilter = ko.observable(null);
	self.visibleOnMapFilter = ko.observable(false);
	self.surveyTypeFilter = ko.observable(null);
	self.speciesFilter = ko.observable(null);

	self.areaLayerFilter = ko.observable(null);
	self.areaFilter = ko.observable(null);
	self.monthsFilter = ko.observableArray(_.pluck(monthOptions, 'index'));
	self.allMonthsFilter = ko.pureComputed({
		read: function() {
			return self.monthsFilter().length === monthOptions.length;
		},
		write: function(value) {
			self.monthsFilter(value ? _.pluck(monthOptions, 'index') : []);
		}
	});
	self.monthsFilterDelayed = ko.pureComputed(function() {
		return self.monthsFilter();
	}).extend({ rateLimit: { timeout: 1500, method: "notifyWhenChangesStop"}});


	self.statsLoading = ko.observable(false);
	self.statsLoaded = ko.observable(false);
	self.stats = ko.observable();

	self.selectedSurvey = ko.observable();
	self.closeSurvey = function() {
		self.selectedSurvey(null);
	};
	self.clickSurvey = function(survey) {
		self.selectedSurvey(survey);
		birdata.survey(survey.id).then(function(fullSurvey) {
			if(self.selectedSurvey() == survey) {
				self.selectedSurvey(fullSurvey);
			}
		});
		return true;
	};

	self.filterCriteriaJSON = ko.pureComputed(function() {
		var criteria = {};

		if(self.afterDateFilter()) {
			criteria.from = moment(self.afterDateFilter()).format("YYYY-MM-DD");
		}

		if(self.beforeDateFilter()) {
			criteria.to = moment(self.beforeDateFilter()).format("YYYY-MM-DD");
		}

		if(self.regionFilter()) {
			criteria.region_id = self.regionFilter().id;
		}

		if(self.programFilter()) {
			criteria.program = self.programFilter().code;
		}

		if(self.visibleOnMapFilter()) {
			var bounds = self.mapBounds();
			if(bounds) {
				criteria.xmin = bounds.getSouthWest().lng();
				criteria.ymin = bounds.getSouthWest().lat();
				criteria.xmax = bounds.getNorthEast().lng();
				criteria.ymax = bounds.getNorthEast().lat();
			}
		}

		if(self.surveyTypeFilter()) {
			criteria.survey_type = self.surveyTypeFilter().code;
		}

		if(self.areaFilter()) {
			criteria.area_id = self.areaFilter().id;
		} else if(self.areaLayerFilter()) {
			criteria.area_layer_id = self.areaLayerFilter().id;
		}

		if(self.monthsFilterDelayed()) {
			if(self.monthsFilterDelayed().length < 12) {
				criteria.months = _.sortBy(self.monthsFilterDelayed(), _.identity).join(",");
			}
		}

		if(self.speciesFilter()) {
			criteria.species_id = self.speciesFilter().id;
		}

		// We are stringifying so that knockout can identify and suppress repeat values
		// (knockout does not deep-compare objects for equality)
		return JSON.stringify(criteria);
	});

	self.filterCriteria = ko.pureComputed(function() {
		return JSON.parse(self.filterCriteriaJSON());
	});

	//---- State handling -------
	var initialState = util.hashParams();
	var haveAppliedInitialState = false;

	function getState() {
		var state = _.extend({}, self.filterCriteria());

		delete state.xmin;
		delete state.xmax;
		delete state.ymin;
		delete state.ymax;

		if(self.visibleOnMapFilter()) {
			state.visible_on_map = self.visibleOnMapFilter();
		}
		if(self.areaLayerFilter()) state.area_layer_id = self.areaLayerFilter().id;

		if(self.map()) {
			var map = self.map();
			self.mapBounds(); // trigger ko binding
			var center = map.getCenter();

			state.map = [center.lat().toFixed(7), center.lng().toFixed(7), map.getZoom()].join('_');
		}

		if(self.tableTabSelection) {
			state.tab = self.tableTabSelection();
		}

		state = _.pick(state, function(v, k) { return !!v; });

		return state;
	}

	function applyInitialState() {
		if(!initialState.area_id) {
			util.defer(function() { haveAppliedInitialState = true; }, 4);
		}

		if(initialState.region_id) {
			self.regionFilter(_.findWhere(self.regions(), {id: +initialState.region_id}));
		}
		if(initialState.program) {
			self.programFilter(_.findWhere(self.programs(), {code: initialState.program}));
		}
		if(initialState.visible_on_map) {
			self.visibleOnMapFilter(initialState.visible_on_map);
		}
		if(initialState.area_layer_id) {
			self.areaLayerFilter(_.findWhere(self.areaLayers(), {id: +initialState.area_layer_id}));
		}
		if(initialState.area_id) {
			var areaInitialised = false;
			self.areas.subscribe(function(areas) {
				if(!areaInitialised) {
					self.areaFilter(_.findWhere(areas, {id: +initialState.area_id}));
					areaInitialised = true;
					util.defer(function() { haveAppliedInitialState = true; }, 4);
				}
			});
		}
		if(initialState.survey_type) {
			var st = _.findWhere(self.surveyTypes(), { code: initialState.survey_type });
			if(st) {
				self.surveyTypeFilter(st);
			}
		}
		if(initialState.from) {
			self.afterDateFilter(initialState.from);
		}
		if(initialState.to) {
			self.beforeDateFilter(initialState.to);
		}
		if(initialState.months) {
			var m = _.map(initialState.months.split(","), function(i) {
				return +i;
			});
			m = _.filter(m, function(i) { return !isNaN(i); });
			self.monthsFilter(m);
		}
	}

	ko.computed(function() {
		history.replaceState({}, '', '#' + util.encodeParams(getState()));
	});
	//---- End state handling ---

	function formattedNumber(obs, key) {
		return ko.pureComputed(function() {
			var o = obs();
			return o && o[key] !== undefined ? util.formatNumber(o[key]) : "";
		});
	}

	self.surveyCount = formattedNumber(self.stats, 'survey_count');
	self.observerCount = formattedNumber(self.stats, 'observer_count');
	self.sharedSiteCount = formattedNumber(self.stats, 'shared_site_count');

	self.sharedSiteTypeCounts = ko.pureComputed(function() {
		var stats = self.stats();
		var surveyTypes = self.surveyTypes();

		if(stats && surveyTypes) {
			return _.chain(stats.shared_site_type_counts)
				.pairs()
				.map(function(pair) {
					var type = _.findWhere(surveyTypes, {code: pair[0]});

					return {
						type: type ? type.name : pair[0],
						count: util.formatNumber(pair[1])
					};
				})
				.value();
		} else {
			return [];
		}
	});

	self.filterCriteriaJSON.subscribe(updateStats);
	updateStats(self.filterCriteriaJSON());

	function updateStats(criteriaJSON) {
		var criteria = JSON.parse(criteriaJSON);

		self.statsLoading(true);
		self.statsLoaded(false);
		birdata.surveyStats(criteria).then(function(stats) {
			if(criteriaJSON != self.filterCriteriaJSON()) {
				console.log("Ignoring stale request: " + criteriaJSON + " vs " + self.filterCriteriaJSON());
				return; // Ignore stale requests
			}
			self.stats(stats);
			self.statsLoaded(true);
		}).finally(function() {
			self.statsLoading(false);
		});
	}

	self.tableTabSelection = ko.observable(initialState.tab || 'shared-site');

	self.tableSelected = function(table) {
		return ko.pureComputed(function() { return self.tableTabSelection() == table; });
	};

	self.selectTable = function(table) {
		return function() { self.tableTabSelection(table); };
	};

	self.currentUser = ko.observable({});
	self.userCanExport = ko.pureComputed(function() {
		var me = self.currentUser();

		var regionId = self.regionFilter() && self.regionFilter().id;
		var areaId = self.areaFilter() && self.areaFilter().id;
		var programCode = self.programFilter() && self.programFilter().code;

		// TODO refactor permission logic to avoid duplication
		return me.is_administrator || _.any(me.permissions, function(p) {
			return (p.role == "admin" && p.type == "region" && p.region_id == regionId) ||
				(p.role == "admin" && p.type == "area" && p.area_id == areaId) ||
				(p.role == "admin" && p.type == "program" && p.program == programCode && (p.area_id == null || p.area_id == areaId));
		});
	});

	// --- SURVEY TABLE

	// TODO: Revisit this pagination implementation. It's not very DRY.

	var pageSize = 100;

	self.surveys = ko.observable([]);
	self.surveysLoading = ko.observable(false);
	self.surveysLoaded = ko.observable(false);

	var moreSurveyResults = true;

	self.surveyTable = new SortableTableViewModel([
		{
			name: 'Survey point',
			value: function(survey) {
				return survey.survey_point.name;
			},
			formatted: function(survey) {
				return this.value(survey) || "(Unnamed)";
			},
			id: 'name'
		}, {
			name: 'Date',
			value: function(survey) {
				return survey.start_date;
			},
			formatted: function(survey) {
				return survey.start_date ? moment(survey.start_date).format("DD/MM/YYYY") : "Unknown";
			},
			id: 'start_date'
		}, {
			name: 'Survey type',
			value: function(survey) {
				var type = _.findWhere(self.surveyTypes(), { code: survey.survey_type });
				if(type) return type.name;
			},
			formatted: function(survey) {
				return this.value(survey);
			},
			id: 'survey_type'
		}, {
			name: 'Accuracy',
			value: function(survey) {
				return survey.survey_point.accuracy_in_metres;
			},
			formatted: function(survey) {
				var x = survey.survey_point.accuracy_in_metres;

				if(x === undefined) {
					return "Unknown";
				} else if(x <= 0) {
					return "Within survey area";
				} else if(x <= 1000) {
					return "Within " + util.formatDistance(survey.survey_point.accuracy_in_metres);
				} else {
					return "> 1km";
				}
			},
			id: "accuracy"
		}, {
			name: 'Species sighted',
			value: function(survey) {
				return survey.species_count;
			},
			formatted: function(survey) {
				return survey.species_count;
			},
			id: "species_count",
			sortable: false
		}
	]);
	self.surveyTable.sortDescriptors([{
		column: 'name',
		ascending: true
	}]);

	function surveyCriteria() {
		var criteriaJSON = self.filterCriteriaJSON();
		var sortDescriptors = self.surveyTable.sortDescriptors();
		var criteria = JSON.parse(criteriaJSON);

		criteria.sort = _.map(sortDescriptors, function(desc) {
			return (desc.ascending ? "" : "-") + desc.column;
		}).reverse().join(",");

		return criteria;
	}


	ko.computed(function() {
		if(self.tableTabSelection() != 'survey') return;

		var criteria = surveyCriteria();

		self.surveys([]);
		moreSurveyResults = true;
		
		setTimeout(function() {
			loadNextSurveyPage(criteria);
		});
	});

	function loadNextSurveyPage(criteria) {
		if(self.surveysLoading()) {
			console.log("warning: attempted to load next page while still loading");
			return;
		}

		self.surveysLoading(true);
		self.surveysLoaded(false);

		var offset = self.surveys().length;

		var pagedCriteria = { ...criteria };

		pagedCriteria.offset = offset;
		pagedCriteria.limit = pageSize;

		var token = {};
		loadNextSurveyPage.lastToken = token;

		birdata.surveys(pagedCriteria).then(function(surveys) {
			if(loadNextSurveyPage.lastToken != token) return;

			if(surveys.length == 0) moreSurveyResults = false;

			self.surveys(self.surveys().concat(surveys));
			self.surveysLoaded(true);
		}).finally(function(){
			self.surveysLoading(false);
		});
	}

	self.surveyScrollPosition = ko.observable(0);
	self.surveyScrollPosition.subscribe(function(scrollPosition) {
		if(!self.surveysLoading() && scrollPosition > 90 && moreSurveyResults) {
			loadNextSurveyPage(surveyCriteria());
		}
	});

	// --- SHARED SITE TABLE

	self.siteSummaries = ko.observable(false);
	self.siteSummariesLoading = ko.observable(false);
	self.siteSummariesLoaded = ko.observable(false);

	var moreSiteSummaryResults = true;

	self.sharedSiteTable = new SortableTableViewModel([
		{
			name: 'Shared site',
			value: function(summary) {
				var ss = summary.shared_site;
				if(ss && ss.name) {
					return ss.name.trim().toLowerCase();
				} else {
					return "";
				}
			},
			formatted: function(summary) {
				var ss = summary.shared_site;
				if(ss) {
					return ss.name || "(Unnamed)";
				} else {
					return "(Not within a shared site)";
				}
			},
			id: 'name'
		}, {
			name: 'Surveys',
			value: function(summary) { return summary.survey_count; },
			formatted: function(summary) { return util.formatNumber(summary.survey_count); },
			id: 'surveys'
		}, {
			name: 'Last Surveyed',
			value: function(summary) { return summary.last_surveyed || ''; },
			formatted: function(summary) {
				return summary.last_surveyed ? moment(summary.last_surveyed).format("DD/MM/YYYY") : '';
			},
			id: 'last_surveyed'
		}, {
			name: 'Survey type',
			value: function(summary) {
				var ss = summary.shared_site;
				if(ss && ss.survey_type && self.surveyTypes()) {
					var type = _.findWhere(self.surveyTypes(), { code: ss.survey_type });
					if(type) return type.name;
				}
				return '';
			},
			formatted: function(summary) {
				return this.value(summary);
			},
			id: 'survey_type'
		}
	]);
	self.sharedSiteTable.sortDescriptors([{
		column: 'name',
		ascending: true
	}]);

	// Not entirely happy with this pagination implementation.
	// It works fine, but feel it could be much more elegant, and would be nice to extract the
	// pagination aspect into a reusable component.

	function siteSummaryCriteria() {
		var criteriaJSON = self.filterCriteriaJSON();
		var sortDescriptors = self.sharedSiteTable.sortDescriptors();
		var criteria = JSON.parse(criteriaJSON);

		criteria.sort = _.map(sortDescriptors, function(desc) {
			return (desc.ascending ? "" : "-") + desc.column;
		}).reverse().join(",");

		return criteria;
	}

	ko.computed(function() {
		if(self.tableTabSelection() != 'shared-site') return;

		var criteria = siteSummaryCriteria();

		self.siteSummaries([]);
		moreSiteSummaryResults = true;
		
		setTimeout(function() {
			loadNextSiteSummaryPage(criteria);
		});
	});

	function loadNextSiteSummaryPage(criteria) {
		if(self.siteSummariesLoading()) {
			console.log("warning: attempted to load next page while still loading");
			return;
		}

		self.siteSummariesLoading(true);
		self.siteSummariesLoaded(false);

		var offset = self.siteSummaries().length;

		var pagedCriteria = { ...criteria };

		pagedCriteria.offset = offset;
		pagedCriteria.limit = pageSize;

		var token = {};
		loadNextSiteSummaryPage.lastToken = token;

		birdata.surveySharedSiteSummaries(pagedCriteria).then(function(summaries) {
			if(loadNextSiteSummaryPage.lastToken != token) return;

			if(summaries.length == 0) moreSiteSummaryResults = false;

			self.siteSummaries(self.siteSummaries().concat(summaries));
			self.siteSummariesLoaded(true);
		}).finally(function(){
			self.siteSummariesLoading(false);
		});
	}

	self.siteSummaryScrollPosition = ko.observable(0);
	self.siteSummaryScrollPosition.subscribe(function(scrollPosition) {
		if(!self.siteSummariesLoading() && scrollPosition > 90 && moreSiteSummaryResults) {
			loadNextSiteSummaryPage(siteSummaryCriteria());
		}
	});

	// --------

	self.regions = ko.observable([]);
	self.programs = ko.observable([]);
	self.areaLayers = ko.observable([]);
	self.areas = ko.observable([]);
	self.surveyTypes = ko.observable([]);
	self.months = monthOptions;

	Promise.all([
		birdata.lookup('program'),
		birdata.regions(),
		birdata.areaLayers(),
		birdata.lookup('survey_type'),
		birdata.speciesList(),
		birdata.me(true).noLoginRedirect().catch(function() { return {}; }),
		programs()
	]).then(function([programs, regions, areaLayers, surveyTypes, speciesList, me, programLogic]) {
			// TODO: move this logic to somewhere more central
			self.programs(_.filter(programs, function(p) {
				if(p.code == 'sop') {
					return !!_.findWhere(me.permissions, { program: 'sop' });
				}
				if(p.code == 'powl') {
					return me.is_administrator || !!_.findWhere(me.permissions, { program: 'powl', role: 'admin' });
				}
				var pl = programLogic.find(p.code);
				if(pl && pl.canFilterBy) {
					return pl.canFilterBy(me);
				}

				return true;
			}));

			self.regions(regions);

			self.areaLayers(_.filter(areaLayers, function(layer) {
				// Only Admins can see RL containment layer
				if(layer.code == "rl_containment") {
					return me && (me.is_administrator || _.findWhere(me.permissions, {role: "admin"}));
				} else {
					return true;
				}
			}));

			// Breeding territory monitoring is often sensitive
			self.surveyTypes(_.filter(surveyTypes, function(x) { return x.code != 'breeding_territory_monitoring' }));

			applyInitialState();

			if(me) {
				self.currentUser(me);
				self.isAdmin(me.is_administrator || _.findWhere(me.permissions, { role: "admin" }));
			}

			self.loaded(true);
		});

	self.areaLayerFilter.subscribe(function(areaLayer) {
		self.areaFilter(null);
		self.areas([]);

		if(areaLayer) {
			birdata.areas().then(function(areas) {
				var options = _.chain(areas)
					.where({layer_id: areaLayer.id})
					.sortBy('name')
					.value();

				self.areas(options);
			});
		}
	});

	self.exportStarting = ko.observable(false);

	self.clickedExport = function() {
		// TODO - show activity indicator while getting download URL
		self.exportStarting(true);

		var exportCriteria = { ...self.filterCriteria() };
		exportCriteria.completed = "any";

		birdata.surveysExportURL(exportCriteria, 'csv').then(function(url) {
			document.location.href = url;
		}).finally(function() {
			self.exportStarting(false);
		});
	};

	// ----- MAP

	googleMaps.load().then(function() {
		var center = new google.maps.LatLng(-22.50831, 136.078612);
		var zoom = 4;

		if(initialState.map) {
			var x = initialState.map.split("_");
			center = new google.maps.LatLng(+x[0], +x[1]);
			zoom = +x[2];
		}

		self.mapOptions(googleMaps.defaultOptions({
			center: center,
			zoom: zoom
		}));
	});

	var layer;
	self.filterCriteria.subscribe(function() {
		if(layer) {
			// Add a small delay so that other requests are initiated first.
			// Otherwise the map tile requests can block other more visually important requests
			setTimeout(function() {
				layer.refresh();
			}, 50);
		}
	});

	self.loadingTiles = ko.observable(0);
	self.visibleTiles = ko.observable(0);

	self.tileLoadingProgress = ko.pureComputed(function() {
		var x = self.loadingTiles();
		var n = self.visibleTiles();

		// Note, if loading off-screen tiles due to fast map changes, progress could be
		// negative, so we set a minimum of 0.
		var percent = Math.max(0, Math.round(n == 0 ? 100 : (n - x) * 100 / n));

		return "Loading " + percent + "%";
	}).extend({ rateLimit: 50 });

	self.tilesLoaded = ko.pureComputed(function() {
		return self.loadingTiles() == 0;
	});

	function increment(obs) { obs(obs() + 1); }
	function decrement(obs) { obs(obs() - 1); }

	// Batch marker show/hide operations
	self.loadingTiles.subscribe(function(n) {
		if(n == 0) {
			var map = self.map();
			if(map) {
				_.each(_.keys(pointMarkers), function(key) {
					var marker = pointMarkers[key];
					if(marker.userData.refCount == 0) {
						marker.setMap(null);
						delete pointMarkers[key];
					} else if(marker.getMap() == null) {
						marker.setMap(map);
					}
				});
			}
		}
	});

	// A lookup object containing all visible markers.
	// A reference counting scheme is used to re-use markers over tile loads which minimises visual jarring.
	// Markers with a zero reference count are released at the end of each loading cycle.
	var pointMarkers = {};
	function markerForPoint(p) {
		var key = p.lat + "," + p.lon;
		var m = pointMarkers[key];

		var title = 'Survey point';
		if(p.approx) {
			title += ' (approximate location shown for sensitive species)';
		}

		if(!m) {
			m = new google.maps.Marker({
				position: new google.maps.LatLng(p.lat, p.lon),
				title: title,
				icon: mapStyles.surveyIcon(false, p.approx),
				userData: {
					refCount: 0
				}
			});
			google.maps.event.addListener(m, 'click', onMarkerClick);
			pointMarkers[key] = m;
		}

		return m;
	}

	function onMarkerClick(evt) {
		var marker = this;
		var p = marker.getPosition();
		showInfoWindow(p.lat(), p.lng(), marker);
	}

	var infoWindow;
	function showInfoWindow(lat, lon, marker) {
		if(infoWindow) {
			infoWindow.close();
		}

		if(!marker) {
			marker = markerForPoint({lat: lat, lon: lon});
		}

		infoWindow = new google.maps.InfoWindow({
			content: h("div", "Loading...")
		});

		var currentInfoWindow = infoWindow; // used to prevent stale requests

		var criteria = _.extend({lat: lat, lon: lon}, self.filterCriteria());

		birdata.surveys(criteria).then(function(surveys) {
			if(infoWindow != currentInfoWindow) return; // ignore stale request

			var latestDate = _.last(_.pluck(surveys, 'start_date').sort());

			var content = h("div", h("p", "Surveys recorded here: " + surveys.length));

			if(latestDate) {
				content.appendChild(h("p", "Last surveyed: " + moment(latestDate).format("DD/MM/YYYY")));
			}

			infoWindow.setContent(content);
		});


		infoWindow.open(self.map(), marker);
	}

	function setupMap(map) {
		if(setupMap.called) return;
		setupMap.called = true;

		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.dragZoomControl(map));

		var overlay = new DensityMapType({
			tileSize: new google.maps.Size(256, 256),
			map: map,
			criteriaFn: self.filterCriteria,
			visibleTiles: self.visibleTiles,
			loadingTiles: self.loadingTiles,
			events: {
				mousemove: function(evt, info) {
					if(info.latLng) {
						hoverOverlay.position(info.latLng);
						evt.stopPropagation();
					}
				},
				mouseover: function(evt, info) {
					if(info.latLng) {
						var count = evt.target.getAttribute('data-count');
						if(count) {
							hoverOverlay.position(info.latLng);
							hoverOverlay.html(util.formatQuantity(+count, "survey"));
							hoverOverlay.show();
							evt.stopPropagation();
						}
					}
				},
				mouseout: function(evt, info) {
					hoverOverlay.hide();
					evt.stopPropagation();
				}
			}
		});

		google.maps.event.addListener(map, 'zoom_changed', function() {
			hoverOverlay.hide();
		});

		self.filterCriteria.subscribe(function(){
			overlay.refresh();
		});
		map.overlayMapTypes.insertAt(0, overlay);


		layer = tileLayer(map, {
			addTile: function(tile) {
				increment(self.visibleTiles);
				increment(self.loadingTiles);

				var json = self.filterCriteriaJSON();
				tile.markers = [];
				birdata.surveyTile(self.filterCriteria(), tile.x, tile.y, tile.z).then(function(points) {
					if(tile.removed) return;
					if(self.filterCriteriaJSON() != json) return; // stale request

					_.each(points, function(p) {
						var marker = markerForPoint(p);
						marker.userData.refCount++;
						tile.markers.push(marker);
					});
				}).finally(function() {
					decrement(self.loadingTiles);
				});
			},
			removeTile: function(tile) {
				decrement(self.visibleTiles);

				_.each(tile.markers, function(marker) {
					marker.userData.refCount--;
				});
				tile.removed = true;
			},
			minZoom: 12
		});

		function hoverContentPromise(poly) {
			switch(poly.userData.layerID) {
				case "areas":
					return birdata.areas().then(function(areas) {
						var area = _.findWhere(areas, {id: poly.userData.id});
						return area ? area.name : null;
					});
				case "sharedSites":
					return birdata.sharedSite(poly.userData.id).then(function(sharedSite) {
						return sharedSite.name;
					});
			}
		}

		var hoverOverlay = googleMaps.hoverOverlay(map);
		var polygonEvents = {
			mousemove: function(evt) {
				hoverOverlay.position(evt.latLng);
			},
			mouseover: function(evt) {
				var featureID = hoverOverlay.featureID = this.userData.id;
				var layerID = hoverOverlay.layerID = this.userData.layerID;

				hoverContentPromise(this).then(function(content) {
					if(hoverOverlay.featureID != featureID || hoverOverlay.layerID != layerID) return; // ignore stale
					
					hoverOverlay.html(content);
					hoverOverlay.position(evt.latLng);
					hoverOverlay.show();
				});
			},
			mouseout: function(evt) {
				hoverOverlay.areaId = null;
				hoverOverlay.hide();
			},
		};

		var areaMapLayer = areasMap.showLayer(map, {
			layerID: "areas",
			polygonEvents: polygonEvents
		});

		ko.computed(function() {
			var areaLayer = self.areaLayerFilter();
			var area = self.areaFilter();
			areaMapLayer.setFeatureFilter(function(feature) {
				var attrs = feature.userData && feature.userData.attributes;
				if(!attrs) return true; //Needed?
				if(!areaLayer) return false;
				if(!area) return areaLayer.id == attrs.layer_id;
				else return area.id == attrs.id;
			});
		});

		sharedSitesMap.showLayer(map, {
			layerID: "sharedSites",
			polygonEvents: polygonEvents
		});

		var regionMapLayer = regionsMap.showLayer(map, {
			layerID: "regions"
		});

		ko.computed(function() {
			var region = self.regionFilter();
			regionMapLayer.setFeatureFilter(function(feature) {
				var attrs = feature.userData && feature.userData.attributes;
				if(!attrs) return true; //Needed?
				if(!region) return false;
				return region.id == attrs.id;
			});
		});

		google.maps.event.addListener(map, 'idle', function() {
			self.mapBounds(map.getBounds());
		});
		self.mapBounds(map.getBounds());

		// Auto zoom to area/region
		ko.computed(function() {
			var area = self.areaFilter();
			var region = self.regionFilter();

			if(!haveAppliedInitialState) return;

			var bounds;
			if(area) bounds = googleMaps.bounds(area.bounds, bounds);
			if(region) bounds = googleMaps.bounds(region.bounds, bounds);
			if(bounds) map.fitBounds(bounds);
		});

	}

	ko.computed(function() {
		var map = self.map();
		if(map) setupMap(map);
	});
};

ko.components.register('programs-regions', {
	viewModel: function(params) {
		return new ProgramsRegionsViewModel(params);
	},
	template: require('../../templates/programs-regions.html')
});

export default ProgramsRegionsViewModel;
